import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  MultiImageReports,
  NavigationHotspot,
  Observation,
  PointImage,
  ProjectFloorPoint,
} from '../../models/project.model';
import { startCase, upperFirst } from 'lodash-es';
import { TranslocoService } from '@ngneat/transloco';
import { GStatic } from '../../services/GStatic';
import { DebugViewService } from '../../services/debug-view.service';
import { Project360Service } from '../p-dashboard/project360.service';
import { AuthService } from '../../core/auth/auth.service';
import { REPORT_TYPE } from './const';
import { SorryDialogComponent } from './components/sorry-dialog/sorry-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FloorPopupComponent } from './components/floor-popup/floor-popup.component';
import { ObservationRefactor } from '../../models/observations.model';
import { ProjectsService } from '../../services/projects.service';
import { TourGuideModel } from '../../models/tour-guide.model';
import { TourGuideDialogComponent } from '../projects/tour-guide-dialog/tour-guide-dialog.component';
import { TitleCasePipe } from '@angular/common';
import { UserService } from 'app/core/user/user.service';
import * as THREE from 'three';
import {
  getViewer360Image,
  isViewer360PlaceholderImage,
} from 'app/shared/utils/branded-placeholder.util';
import {
  CAMERA_EYE_HEIGHT_FT,
  CAMERA_FLOOR_CLEARANCE_FT,
  CAMERA_FLOOR_SNAP_RANGE_FT,
  feetToModelUnits,
} from 'app/shared/utils/bim-units.util';
import { observationPinAngles } from 'app/shared/utils/ai-degrees';
import { isAiGenerated, ppeConfidencePercent } from 'app/services/PPE/ppe.models';

export type Screen = 'SC1' | 'SC2';

/** Maps status (English or localized ar/ru) to canonical icon key so 360 viewer and floor plan show correct color in all locales. */
const _STATUS_TO_ICON_KEY: Record<string, string> = {
  pending: 'pending',
  in_progress: 'in_progress',
  completed: 'completed',
  closed: 'closed',
  draft: 'draft',
  // Arabic
  'قيد التنفيذ': 'in_progress',
  'قيد الانتظار': 'pending',
  مكتمل: 'completed',
  مغلق: 'closed',
  مسودة: 'draft',
  // Russian (API may return status or status_label in these forms)
  'в процессе': 'in_progress',
  'в ожидании': 'pending',
  ожидает: 'pending',
  завершено: 'completed',
  закрыто: 'closed',
  черновик: 'draft',
};

/** Case-insensitive lookup for status → icon key (RUS-127: API may return varying casing for Russian). */
export function statusToIconKey(status: string): string {
  const normalized = status?.trim();
  return (
    _STATUS_TO_ICON_KEY[normalized] ?? _STATUS_TO_ICON_KEY[normalized?.toLowerCase()] ?? 'helmet'
  );
}

const _AI_PIN_STATUS_KEYS = new Set(['pending', 'in_progress', 'completed', 'closed', 'draft']);

/**
 * PPE auto-detection (V2CORE-2901): 360 pin icon for an AI observation.
 * One AI icon per status (same colours as hand-made pins, plus the AI mark).
 * Unknown statuses fall back to the category icon; HOME titles AI rows
 * "Not wearing a Safety Helmet" / "Not wearing a Safety Vest".
 */
export function aiObservationPinUrl(observation: {
  title?: string | null;
  status?: string | null;
}): string {
  const statusKey = statusToIconKey(observation?.status ?? '');
  if (_AI_PIN_STATUS_KEYS.has(statusKey)) {
    return `assets/krpano/media/alert-ai-${statusKey}.png`;
  }
  const title = (observation?.title ?? '').toLowerCase();
  return /\bvest\b/.test(title)
    ? 'assets/krpano/media/alert-ai-vest.png'
    : 'assets/krpano/media/alert-ai-helmet.png';
}

/** PPE auto-detection (V2CORE-2901): "AI · 91%" pin hover line, or "AI" without confidence. */
export function aiObservationPinHover(
  observation: { ppe_detection_confidence?: number | string | null },
  translate: (key: string, params?: Record<string, unknown>) => string,
): string {
  const percent = ppeConfidencePercent(observation?.ppe_detection_confidence);
  return percent === null
    ? translate('ppe.ai.pin_hover_no_confidence')
    : translate('ppe.ai.pin_hover', { confidence: percent });
}

export interface PanoBoundary {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

@Injectable({
  providedIn: 'root',
})
export class PanosService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private rafId: number | null = null;
  isMobile = GStatic.isMobile();
  pointsOnMap: ProjectFloorPoint[] = [];
  pointsOnMapSplit: ProjectFloorPoint[] = [];
  minZoomSlider: number;
  valZoomSlider: number;
  dialogsRef: { [key: string]: MatDialogRef<any, any> } = {};
  // Helpers
  timer: ReturnType<typeof setTimeout>;
  _panosList: any = {};
  public viewer: any;
  lockBim: boolean = true;
  public maxZoomSlider: number;
  isLookToObservation: boolean = false;
  hasMultipleImages: boolean = false;
  multiImageReports: any;
  fullPath: any = false;
  private _panosImgs: any = {};
  private _loaded: any = {};
  private _isLoaded = new BehaviorSubject<{ [key: string]: boolean }>({});
  readonly isLoaded = this._isLoaded.asObservable();
  private _loading: boolean = true;
  private _isLoading = new BehaviorSubject<boolean>(this._loading);
  readonly isLoading = this._isLoading.asObservable();
  private _event = new BehaviorSubject<{ eventName?: string; data?: any }>({});
  readonly event = this._event.asObservable();
  private pointScale: number;
  private isAnimating: any = false;
  private targetPosition: THREE.Vector3;
  private targetLookAt: any;
  private pointCount: any = 0;
  private caclulatedPointSize: number;
  private activePoint: ProjectFloorPoint[] = [null, null];
  private _mainMiniMapLoaded: boolean = false;
  currentOffset: number = 0;

  private isNwcClientContext(): boolean {
    return this._project.isNwcClientSync() || this.userService.isNwcClientSync();
  }

  private getRenderableViewerImage(src: string): string {
    return getViewer360Image(src, this.isNwcClientContext()) || src;
  }

  constructor(
    private toaster: ToastrService,
    private dialog: MatDialog,
    private _auth: AuthService,
    private _transloco: TranslocoService,
    private translocoService: TranslocoService,
    private _debugView: DebugViewService,
    private _project: Project360Service,
    private userService: UserService,
    private _projectsApi: ProjectsService,
  ) {
    // Subscribe to language changes to update post-processing labels
    this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this._mainPano && this._mainPano.set) {
        this.updatePostProcessingLabels();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _mainPano: any = {};

  get mainPano(): any {
    return this._mainPano;
  }

  get mainMiniMap() {
    return this._mainMiniMapLoaded;
  }

  private _secondPano: any = null;

  get secondPano(): any {
    // return Object.entries(this._panosList)[1] && Object.entries(this._panosList)[1][1];
    return this._secondPano;
  }

  get panos(): any {
    return this._panosList;
  }

  get snapLoaded(): { [key: string]: boolean } {
    return this._loaded;
  }

  private readonly _isBimSplitBootstrapping$ = new BehaviorSubject<boolean>(false);
  get isBimSplitBootstrapping(): boolean {
    return this._isBimSplitBootstrapping$.value;
  }
  set isBimSplitBootstrapping(value: boolean) {
    this._isBimSplitBootstrapping$.next(value);
  }
  get isBimSplitBootstrapping$() {
    return this._isBimSplitBootstrapping$.asObservable();
  }

  // first embed krpano to the view and linked it to the div by id
  embedPano(
    id: string,
    target: string,
    xml: string = 'assets/krpano/pano2.xml',
    priority: number = 3,
  ): void {
    this._panosImgs[id] = undefined;
    (window as any).embedpano({
      html5: 'only',
      id: id,
      target: target,
      xml: xml,
      consolelog: true, // trace krpano messages also to the browser console
      vars: { syncside: id, syncviewer: '' },
      passQueryParameters: true,
      onready: (krpano_interface) => {
        (window as any)['kr_' + id] = krpano_interface;
        this._panosList[id] = krpano_interface;
        // Set translated "loading..." text for krpano screenshot plugin (RUS-127)
        krpano_interface.set('loading_text', this.translocoService.translate('Loading...'));
        if (priority === 0) {
          this._mainPano = this._panosList[id];
          // Update post-processing labels when pano is ready
          // Use multiple timeouts to ensure XML is fully loaded and plugins are initialized
          setTimeout(() => {
            this.updatePostProcessingLabels();
          }, 100);
          setTimeout(() => {
            this.updatePostProcessingLabels();
          }, 300);
          setTimeout(() => {
            this.updatePostProcessingLabels();
          }, 600);
          setTimeout(() => {
            this.updatePostProcessingLabels();
          }, 1000);
          setTimeout(() => {
            this.updatePostProcessingLabels();
          }, 2000);
        } else if (priority === 1) {
          this._secondPano = this._panosList[id];
        }
        this._event.next({
          eventName: 'onEmbedPanoReady',
          data: krpano_interface,
        });
      },
    });
  }

  getPano(id: string): any {
    return this._panosList[id];
  }

  loadImageForTimeLapse(
    id: string,
    src: PointImage,
    callBack?: (pano: any) => void,
    forTracking = 0,
    point?: ProjectFloorPoint,
    hotspot?: NavigationHotspot,
    isTimelapse?: boolean,
  ): void {
    GStatic.log('loadImage Tracking ', id, forTracking);
    if (!this._panosList[id]) {
      return;
    }
    if (src.image_360 === this._panosImgs[id]) {
      if (src.image_360 === 'assets/krpano/media/space.png') {
        this._event.next({
          eventName: 'onImageLoadComplete',
          data: this._panosList[id],
        });
        if (callBack) {
          callBack(this._panosList[id]);
        }
      }

      return;
    }

    this._panosImgs[id] = src.image_360;
    const currentImage = src;
    const hasPreviewImage =
      currentImage.image_360_thumb && !isViewer360PlaceholderImage(currentImage.image_360_thumb);
    if (hasPreviewImage) {
      this.loadNewItem(id);
      this.loadImageOnViewer(currentImage.image_360_thumb, id, hotspot, callBack);
      setTimeout(() => {
        fetch(src.image_360)
          .then((res) => res.blob())
          .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);

            //Load into krpano from blob URL
            if (src.image_360 === currentImage.image_360) {
              const currentPanoAthAngle = this._panosList[id].get('view.hlookat'); //84
              const currentPanoAtvAngle = this._panosList[id].get('view.vlookat');
              this.loadImageOnViewer(blobUrl, id, hotspot, callBack, null, true);
              this._panosList[id].call(`tween(view.hlookat, ${currentPanoAthAngle})`);
              this._panosList[id].call(`tween(view.vlookat, ${currentPanoAtvAngle})`);
            }
            // (Optional) Revoke the blob later to free memory
            // Set a timeout or listen for krpano 'onloadcomplete' event
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
            }, 10000); // or after you're sure it's in use
          });
      }, 1000);
    } else {
      this.loadNewItem(id);
      this.loadImageOnViewer(currentImage.image_360, id, hotspot, callBack);
    }
  }

  loadImage(
    id: string,
    src: string,
    callBack?: (pano: any) => void,
    forTracking = 0,
    point?: ProjectFloorPoint,
    hotspot?: NavigationHotspot,
    isTimelapse?: boolean,
  ): void {
    const source = this._mainPano;
    GStatic.log('loadImage Tracking ', id, forTracking);
    if (!this._panosList[id]) {
      return;
    }
    if (src === this._panosImgs[id]) {
      if (src === 'assets/krpano/media/space.png') {
        this._event.next({
          eventName: 'onImageLoadComplete',
          data: this._panosList[id],
        });
        if (callBack) {
          callBack(this._panosList[id]);
        }
      }
      return;
    }

    this._panosImgs[id] = src;
    const currentImage =
      id === 'krpanoSecondViewer' ? this._project.secondPImg : this._project.currentPImg;
    this.loadNewItem(id);
    const hasPreviewImage =
      currentImage.image_360_thumb && !isViewer360PlaceholderImage(currentImage.image_360_thumb);
    if (hasPreviewImage) {
      // Have to fetch it before hand to make loading work correctly as krpano event for preview loading complete doesnt work
      fetch(currentImage.image_360_thumb)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.loadImageOnViewer(currentImage.image_360, id, hotspot, callBack, blobUrl);
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 10000); // or after you're sure it's in use
        });
    } else {
      // In case there is no thumbnail
      this.loadImageOnViewer(currentImage.image_360, id, hotspot, callBack, null);
    }
  }

  loadImageOnViewer(
    newSrc: any,
    id: string,
    hotspot,
    callBack,
    thumb?: string,
    secondTime: boolean = false,
  ) {
    const source = this._mainPano;

    let newPanoAthAngle = 0;
    let newPanoAtvAngle = 0;

    if (hotspot) {
      const currentPanoAthAngle = source.get('view.hlookat'); //84
      const currentPanoAtvAngle = source.get('view.vlookat');
      const currentImage = this._project.getPImg(hotspot.current_point_uuid);
      const nextImage = this._project.getPImg(hotspot.next_point_uuid);
      const ha1 = currentImage.heading_angle;
      const ha2 = nextImage.heading_angle;
      newPanoAthAngle = ha1 - ha2 + currentPanoAthAngle;

      newPanoAtvAngle = currentPanoAtvAngle;
    }

    // get scale of viewer and open the second/main image on that scale
    const fov = parseFloat(this._panosList[id].get('view.fov'));
    const renderSrc = this.getRenderableViewerImage(newSrc);
    const renderThumb = thumb ? this.getRenderableViewerImage(thumb) : thumb;

    if (!hotspot) {
      this._panosList[id].call(
        `loadxml(${this.getXMLFormat(renderSrc, 80, renderThumb)}, null, MERGE, BLEND(0.5));`,
      );
    } else {
      this._panosList[id].call(
        `loadxml(${this.getXMLFormat(
          renderSrc,
          80,
          renderThumb,
        )}, null, MERGE, ZOOMBLEND(1.0, 2.0, easeInOutSine));`,
      );
    }
    // console.log(newSrc, secondTime);

    if (!secondTime) {
      source.call(`tween(view.hlookat, ${newPanoAthAngle})`);
      source.call(`tween(view.vlookat, ${newPanoAtvAngle})`);
    }

    const newPano = source.get('view.hlookat');

    const funcOnLoad = thumb
      ? 'events[myevents].onpreviewcomplete'
      : 'events[myevents].onloadcomplete';

    this._panosList[id].set(funcOnLoad, () => {
      this.itemLoaded(id, thumb);
      thumb = null;
      this._event.next({
        eventName: 'onImageLoadComplete',
        data: this._panosList[id],
      });

      // Re-add radar if it was present and this is the main image loading (not thumbnail)
      if (
        !thumb &&
        this.getRadarPermission() &&
        !!this.activePoint[0] &&
        id === 'krpanoMainViewer'
      ) {
        setTimeout(() => {
          this.addRadar(this._mainPano);
        }, 100);
      }

      if (callBack) {
        callBack(this._panosList[id]);
      }
    });

    if (!this.isLookToObservation && !hotspot && !secondTime) {
      setTimeout(() => {
        this.mainPano.call(
          `tween(view.hlookat, ${this._project?.currentPImg?.horizontal_angle || 0})`,
        );
        this.mainPano.call(
          `tween(view.vlookat, ${this._project?.currentPImg?.vertical_angle || 0})`,
        );
      }, 500);
    }

    if (!secondTime) {
      this.setOffsetDefaultView(
        this._project?.secondPImg?.horizontal_angle - this._project?.currentPImg?.horizontal_angle,
      );
    }
    this._panosList[id].set('events[myevents].keep', true);
  }

  getLoadedImg(id): string {
    return this.getPano(id).get('image.sphere.url');
  }

  setMainPano(panoId: string): void {
    this._mainPano = this.getPano(panoId);
  }

  // Sync

  async setObservationView(id: string, observationId: string): Promise<void> {
    this._project.getObservationDetailsAPI(observationId).subscribe((response) => {
      if (response.observation_details?.deleted_at) {
        const title = this._transloco.translate('Sorry for the inconvenience');
        const message = this._transloco.translate(
          'We are sorry to inform you, the observation you are trying to access has been deleted by the creator, or the admin',
        );
        this.dialog.open(SorryDialogComponent, {
          data: { title, message },
          width: '300px',
        });
      } else {
        setTimeout((e) => {
          this._panosList[id].call(`looktohotspot('t_${observationId}', 50, smooth(150,150,130));`);
        }, 500);
      }
    });
  }

  //#region  -- syncPano
  syncPano(masterViewId: string, slaveViewId: string): void {
    this._panosList[masterViewId].set('syncviewer', slaveViewId);
  }

  syncPanos(firstViewId: string, secondViewId: string, sync: boolean = true): void {
    if (sync) {
      this._panosList[firstViewId].set('syncviewer', secondViewId);
      this._panosList[secondViewId].set('syncviewer', firstViewId);
      // this._panosList[secondViewId].call(`tween(view.hlookat, ${180}`);
    } else {
      this._panosList[firstViewId].set('syncviewer', '');
      this._panosList[secondViewId].set('syncviewer', '');
    }
  }

  syncPanosList(panoIdsList: string[], sync: boolean = true): void {
    if (!panoIdsList) {
      return;
    }
    panoIdsList.forEach((mainId) => {
      if (!this._panosList[mainId]) {
        return;
      }
      const arr = [];
      panoIdsList.forEach((otherId) => {
        if (mainId !== otherId) {
          arr.push(otherId);
        }
      });
      this._panosList[mainId].set('syncviewer', sync ? arr : '');
    });
  }

  //#endregion

  syncPanosListToMaster(masterViewId: string, panoIdsList: string[], sync: boolean = true): void {
    if (!panoIdsList) {
      return;
    }

    if (!this._panosList[masterViewId]) {
      return;
    }
    const arr = [];
    panoIdsList.forEach((otherId) => {
      if (masterViewId !== otherId) {
        arr.push(otherId);
      }
    });
    this._panosList[masterViewId].set('syncviewer', sync ? arr : '');
  }

  getXMLFormat(src: string, fov: number = 80, srcThumb: string): any {
    return `
            <krpano>
            <!--Using builtin preview for main and spli screen viewer-->
            <preview url="${srcThumb}" type="sphere"/>
            <image><sphere id="sphere" url="${src}" /></image>
            <contextmenu fullscreen="false" versioninfo="false">
              <item name="kr" caption="KRPANO" />
<!--              <item name="fs" caption="FULLSCREEN" />-->
<!--              <item name="cc" caption="Open Observation" onclick="js(addObservationHotSpot());"  separator="true" />-->
              </contextmenu>
            <view hlookat="0" vlookat="0" fov="${fov}" fovmax="120" fovmin="10" distortion="0.0" />

            </krpano>`;
  }

  stopAnimate(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  animate(
    from: number = 0,
    to: number = 300,
    fun: (x: number) => void,
    duration: number = 500,
  ): ReturnType<typeof setTimeout> {
    clearInterval(this.timer);
    const easeInOutQuart = (t, b, c, d) => {
      // eslint-disable-next-line no-cond-assign
      if ((t /= d / 2) < 1) {
        return (c / 2) * t * t * t * t + b;
      }
      return (-c / 2) * ((t -= 2) * t * t * t - 2) + b;
    };

    const start = new Date().getTime();
    this.timer = setInterval(() => {
      const time = new Date().getTime() - start;
      const x = easeInOutQuart(time, from, to - from, duration);
      fun(x);
      if (time >= duration) {
        clearInterval(this.timer);
        fun(to);
      }
    }, 1000 / 60);

    return this.timer;
  }

  // Loading Functions
  loadNewItem(id: string): void {
    if (this._loading === false) {
      this._loaded = {};
      this._loading = true;
    }
    this._loaded[id] = false;
    this._isLoaded.next({ ...this._loaded });
    this._isLoading.next(this._loading);
  }

  itemLoaded(id: string, thumb: string): void {
    this._loaded[id] = true;
    this._isLoaded.next({ ...this._loaded });

    const notLoaded = Object.entries(this._loaded).filter(([key, value]) => value === false);
    GStatic.log(
      thumb ? 'events[myevents].onpreviewcomplete' : 'events[myevents].onloadcomplete',
      this._loading,
      {
        ...this._loaded,
      },
    );

    if (notLoaded.length === 0) {
      this._loading = false;
      this._isLoading.next(this._loading);
      GStatic.log(
        thumb
          ? 'events[myevents].onpreviewcomplete ___ completed'
          : 'events[myevents].onloadcomplete ___ completed',
        this._loading,
        {
          ...this._loaded,
        },
      );
      // this._isLoaded.complete();
      // this._isLoaded.next({});
    }
  }

  /* ******************
   *  *** Floor Map ***
   *  ****************** */

  map_addMapLayer(
    src: string,
    boundry: PanoBoundary,
    callBack?: (panoSource: any) => void,
    split?: boolean,
  ): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    source.call('addlayer(box)');
    source.set('layer[box].align', 'righttop');
    source.set('layer[box].width', boundry.width);
    source.set('layer[box].height', boundry.height);
    source.set('layer[box].x', split ? 15 : boundry.x);
    source.set('layer[box].y', boundry.y);
    source.set('layer[box].type', 'container');
    source.set('layer[box].bgcolor', this._auth._persona.colorAccent1.replace(/#/g, '0x'));
    source.set('layer[box].bgalpha', '0.1');
    source.set('layer[box].bgcapture', 'true');
    source.set('layer[box].keep', 'true');
    source.set('layer[box].maskchildren', 'true');
    source.set('layer[box].url', 'assets/krpano/media/map_loaging.png');

    source.call('addlayer(map)');
    source.set('layer[map].y', 0.1);
    source.set('layer[map].x', 0.1);
    source.set('layer[map].handcursor', false);
    source.set('layer[map].scalechildren', true);
    source.set('layer[map].keep', true);
    source.set('layer[map].parent', 'box');
    source.set('layer[map].align', 'center');

    source.set('layer[box].safearea', true);
    source.set('layer[map].url', src); // 'assets/images/grid.png' ||
    source.set('layer[map].ondown', 'draglayer();');

    source.call('addlayer(loading)');
    source.set('layer[loading].url', 'assets/krpano/media/loading.svg');
    source.set('layer[loading].parent', 'box');
    source.set('layer[loading].keep', true);
    source.set('layer[loading].align', 'center');
    source.set('layer[loading].scale', '0.5');
    setTimeout((e) => {
      source.call('removelayer(loading)');
    }, 5000);

    // source.set('layer[loading].y', 50);

    source.set('layer[map].onloaded', () => {
      source.call('removelayer(loading)');
      const box = source.get('layer[box]');
      const map = source.get('layer[map]');

      const boxHeight = box.height;
      const mapAspectRatio = map.imagewidth / map.imageheight;

      // Calculate the width based on the desired height (100% of box layer height) and the map's aspect ratio
      const adjustedWidth = boxHeight * mapAspectRatio;

      // Center the map horizontally within the box
      const offsetX = (box.width - adjustedWidth) / 2;
      const boxWidth = box.width;
      // Calculate the maximum width based on the desired height (100% of box layer height) and the map's aspect ratio
      const maxWidth = boxHeight * mapAspectRatio;

      // Check if the width of the map is greater than the width of the box
      if (maxWidth > boxWidth) {
        // If the map is wider than the box, adjust the width and height to fit the box width
        const adjustedWidth = boxWidth;
        const adjustedHeight = boxWidth / mapAspectRatio;
        source.set('layer[map].width', adjustedWidth);
        source.set('layer[map].height', adjustedHeight);
      } else {
        // If the map fits within the box width, use the original width and height
        source.set('layer[map].width', adjustedWidth);
        source.set('layer[map].height', boxHeight);
      }

      this.minZoomSlider = 100;
      this.maxZoomSlider = 1000;
      this.valZoomSlider = this.minZoomSlider;
      const event = { value: 100 }; // Example value for the zoom level
      this.map_onZoomChange(event, split);

      this._debugView.addDebug('map.imageheight', map.imageheight);
      this.minZoomSlider = 100;
      this.maxZoomSlider = 1000;
      this.valZoomSlider = this.minZoomSlider;

      if (callBack) {
        callBack(source);
      }
    });

    if (!split) {
      //enables split screen once the mini map is loaded first time
      this._mainMiniMapLoaded = true;
    }
  }

  isLoadingMap(split: boolean, loading: boolean) {
    const source = split ? this._secondPano : this._mainPano;
    if (loading) {
      source.call('addlayer(loading)');
      source.set('layer[loading].url', 'assets/krpano/media/loading.svg');
      source.set('layer[loading].parent', 'box');
      source.set('layer[loading].keep', true);
      source.set('layer[loading].align', 'center');
      source.set('layer[loading].scale', '0.5');
    } else {
      source.call('removelayer(loading)');
    }
  }

  /* ******************
   *  *** Water Mark ***
   *  ****************** */

  map_addWaterMark(source): void {
    setTimeout(async () => {
      const screenWidth =
        window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      const screenHeight =
        window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
      const watermarkSize = Math.max(screenWidth, screenHeight) / 16; // set the size of the watermarks
      const horizontalSpacing = watermarkSize * 2.5; // set the horizontal spacing between the watermarks
      const verticalSpacing = watermarkSize * 3.5; // set the vertical spacing between the watermarks
      const numRows = Math.ceil(screenHeight / verticalSpacing); // calculate the number of rows needed to cover the screen height
      const numCols = Math.ceil(screenWidth / horizontalSpacing); // calculate the number of columns needed to cover the screen width
      const totalWatermarks = numRows * numCols; // calculate the total number of watermarks needed to cover the screen

      const user = await this.userService.getCurrentUser();
      for (let i = 0; i < totalWatermarks; i++) {
        const viewerId = `viewer${i}`;
        const hotspotId = `watermark${i}`;
        const row = Math.floor(i / numCols);
        const col = i % numCols;
        const rowOffset = row % 2 === 0 ? 0 : horizontalSpacing / 2;
        const ath = col * horizontalSpacing + rowOffset;
        const atv = row * verticalSpacing;

        source.call(`addlayer(${hotspotId}, ${viewerId})`);
        source.set(
          `layer[${hotspotId}].html`,
          `${user.name} <br> ${user.email}  <br> ${user.client}`,
        );
        source.set(`layer[${hotspotId}].type`, `text`);
        source.set(
          `layer[${hotspotId}].css`,
          `transform:rotate(-45deg);text-align:center;font-family:Tajawal;z-index:-1;font-size:19px;color:rgba(255,255,255,0.6);opacity:0.6`,
        );
        source.set(`layer[${hotspotId}].handcursor`, false);
        source.set(`layer[${hotspotId}].alpha`, 1);
        source.set(`layer[${hotspotId}].preload`, true);
        source.set(`layer[${hotspotId}].zorder`, 1);
        source.set(`layer[${hotspotId}].x`, ath - watermarkSize / 2);
        source.set(`layer[${hotspotId}].y`, atv - watermarkSize / 2);
        source.set(`layer[${hotspotId}].keep`, true);
        source.set(`layer[${hotspotId}].bgalpha`, 0.0);
        source.set(`layer[${hotspotId}].interactive`, false);
        source.set(`layer[${hotspotId}].capture`, false);
      }
    }, 500);
  }

  setOffsetMatchingPoint(offset: any, split?: boolean) {
    const source = split ? this._secondPano : this._mainPano;
    source.set('report_type', 'video');
    source.set('angle_offset', offset);
  }

  setOffsetDefaultView(offset: any) {
    const source = this._mainPano;
    if (isNaN(offset)) {
      offset = 0;
    }
    source.set('report_type', 'image');
    source.set('horizontal_angle', offset);
  }

  map_onZoomChange(event: any, split?: boolean): void {
    this.resizeMap(event.value, split);
  }

  resizeMap(size: number, split?: boolean): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    const map: any = source.get('layer[map]');
    const box: any = source.get('layer[box]');
    const mapX = map?.x;
    const mapW = map.width;
    const mapScale = map?.scale;
    const mapWpx = mapW * mapScale;

    // zoom with the map with the box anchor
    this._debugView.addDebug('Zoom event & x', size + ' & x: ' + map.x);
    this._debugView.addDebug('Zoom event * 5', size * 5);

    this._debugView.addDebug(
      'mapW Before',
      Math.round(source.get('layer[map].width') * source.get('layer[map].scale')) +
        ' & ' +
        Math.round(source.get('layer[map].x')),
    );
    source.set('layer[map].scale', size / 100);
    const deltaW = ((map.width * map?.scale - mapWpx) / 100 / -50) * map.x;
    source.set('layer[map].x', map.x - deltaW);

    this._debugView.addDebug(
      'mapW after',
      Math.round(source.get('layer[map].width') * source.get('layer[map].scale')) +
        ' & ' +
        Math.round(source.get('layer[map].x')),
    );
    this._debugView.addDebug('map.x', +map.x);
    this._debugView.addDebug('deltaW', +deltaW);
    this._debugView.addDebug('x', +map.x - deltaW);
    /*

                                                                                                                                                                                                                                                    const deltaW = (source.get('layer[map].width') * source.get('layer[map].scale')) - Math.round(mapW * mapScale);
                                                                                                                                                                                                                                                    // source.set('layer[map].x', mapX +  event.value * Math.abs(deltaW) / 2);



                                                                                                                                                                                                                                                    this._debugView.addDebug('mapW delta',
                                                                                                                                                                                                                                                      Math.round(deltaW)
                                                                                                                                                                                                                                                      + ' & ' +
                                                                                                                                                                                                                                                      Math.round(source.get('layer[map].x') - mapX)
                                                                                                                                                                                                                                                    );*/

    // don't move out of the  boundary
    const deltaX = box.width * box.scale - map.width * map?.scale;
    const deltaY = box.height * box.scale - map.height * map?.scale;
    // this._debugView.addDebug('deltaX', deltaX);
    // this._debugView.addDebug('deltaY', deltaY);
    if (Math.abs(map.x) > Math.abs(deltaX / 2)) {
      source.set('layer[map].x', (deltaX / 2) * -Math.sign(map.x));
    }
    if (Math.abs(map.y) > Math.abs(deltaY / 2)) {
      source.set('layer[map].y', (deltaY / 2) * -Math.sign(map.y));
    }

    split ? this.resizePointsBasedOnMapZoomLevel(true) : this.resizePointsBasedOnMapZoomLevel();
  }

  resizePointsBasedOnMapZoomLevel(split?: boolean): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }

    const resizingPoints = setTimeout(() => {
      clearTimeout(resizingPoints);

      // dynamically resize the points on the map based on the zoom level
      this.caclulatedPointSize = split ? this.calcPointSize(true) : this.calcPointSize();
      if (split) {
        this.pointsOnMapSplit.forEach((point: ProjectFloorPoint) => {
          const poinstSize = this.getPointSize(point);

          source.set(`layer[p_${point.id}].height`, poinstSize);
          source.set(`layer[p_${point.id}].width`, poinstSize);
        });
      } else {
        this.pointsOnMap.forEach((point: ProjectFloorPoint) => {
          const poinstSize = this.getPointSize(point);

          source.set(`layer[p_${point.id}].height`, poinstSize);
          source.set(`layer[p_${point.id}].width`, poinstSize);
        });
      }
    }, 500);
  }

  calcPointSize(split?: boolean): number {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    const zoom = source.get('layer[map].scale');
    const map = source.get('layer[map]');

    const width = map.imagewidth;
    const height = map.imageheight;

    // Calculate aspect ratio
    const aspectRatio = width / height;

    // Determine the base scale factor using a combination of width and height
    const baseScaleFactor = Math.min(300 / width, 200 / height);

    // Adjust the scale factor based on aspect ratio
    let scaleFactor = baseScaleFactor * (aspectRatio > 1 ? 1 : 2 / aspectRatio);

    // If aspect ratio is close to 1:1, slightly increase the scale factor
    const aspectRatioThreshold = 0.2;

    if (Math.abs(aspectRatio - 1) <= aspectRatioThreshold || Math.abs(aspectRatio - 1) === 1) {
      scaleFactor *= 2;
    }

    // Calculate radius using the adjusted scale factor
    let radius = width * height * scaleFactor * 0.0002;

    // Prevent radius from becoming too small by setting a minimum value
    const minRadius = 10;
    radius = Math.max(radius, minRadius);
    const finalRatio = radius / zoom;

    // Calculate dynamic max limit based on image area
    const imageArea = width * height;
    let maxRatio = 50;

    if (imageArea > 2000000) {
      maxRatio = 120; // allow larger ratio for large images
    } else if (imageArea > 1000000) {
      maxRatio = 80;
    }

    // Clamp the result within min and dynamic max bounds
    if (Math.abs(finalRatio) >= maxRatio) {
      return maxRatio;
    } else if (Math.abs(finalRatio) <= 10) {
      return 10;
    } else {
      return finalRatio;
    }
  }

  getPointSize(point: ProjectFloorPoint): number {
    /**
     * this was for making the selected point bigger, but we decided to keep all points the same size
     *
     * return point.is_selected_point ? this.caclulatedPointSize * 1.7 : this.caclulatedPointSize;
     */
    return this.caclulatedPointSize;
  }

  map_addPoint(point: ProjectFloorPoint, onClick?: (point: any) => void, split?: boolean): void {
    split ? this.pointsOnMapSplit.push(point) : this.pointsOnMap.push(point);
    // if we didn't calculate the point size yet, calculate it
    if (!this.caclulatedPointSize) {
      this.caclulatedPointSize = this.calcPointSize();
    }

    const pName = point.point_name.replace('_', ' ');
    const capitalPname = pName.charAt(0).toUpperCase() + pName.slice(1);
    const pointLabel =
      point.is_deleted || point.is_deleted_in_original
        ? `${capitalPname} (${this._transloco.translate('Deleted')})`
        : capitalPname;
    const source = split ? this._secondPano : this._mainPano;

    if (!source) {
      return;
    }

    if (!source) {
      return;
    }

    source.call(`addlayer(p_${point.id})`);
    source.set(`layer[p_${point.id}].x`, point.point_x);
    source.set(`layer[p_${point.id}].y`, point.point_y);
    source.set(`layer[p_${point.id}].zorder`, 5);
    source.set(`layer[p_${point.id}].parent`, `map`);
    source.set(`layer[p_${point.id}].keep`, true);
    if (point.is_selected_point === 1) {
      setTimeout(() => {
        source.set(`layer[p_${point.id}].url`, `assets/krpano/media/camicon-starred.png`);
      }, 500);
    } else {
      source.set(`layer[p_${point.id}].url`, `assets/krpano/media/camicon.png`);
    }
    source.set(`layer[p_${point.id}].align`, `lefttop`);
    source.set(`layer[p_${point.id}].edge`, `center`);
    source.set(`layer[p_${point.id}].scale`, 1);

    source.set(`layer[p_${point.id}].width`, this.getPointSize(point));
    source.set(`layer[p_${point.id}].height`, this.getPointSize(point));
    // source.set(`layer[p_${point.id}].scale`, 0.5);

    // if (this._project.currentPImg.report.report_type === REPORT_TYPE.VIDEOS){
    //   source.set(`layer[p_${point.id}].width`, point.map_point_radius * 6);
    //   source.set(`layer[p_${point.id}].height`, point.map_point_radius * 6);
    // }else{
    //     source.set(`layer[p_${point.id}].scale`, 0.5);
    // }

    source.set(`layer[p_${point.id}].onhover`, `showtext(${pointLabel},STYLE1);`);
    source.set(`layer[map].onover`, () => {
      source.set('layer[radar].visible', false);
      source.call(`tween(layer[radar].alpha, ${0})`);
    });
    source.set(`layer[map].onout`, () => {
      source.set('layer[radar].visible', true);
      source.call(`tween(layer[radar].alpha, ${0.75},0.2)`);
      source.call(`tween(layer[radar].zorder, ${1})`);
    });
    source.set(`layer[p_${point.id}].alpha`, 1);
    source.set(`layer[p_${point.id}].onclick`, () => {
      this.toaster.clear();
      if (onClick) {
        onClick(point);
      }
    });

    // TODO: check with Omer Saleem whether this is needed or not.
    const recursiveTimeOut = () => {
      setTimeout(() => {
        const res = this.map_point_changeSize(point?.id);
        if (!res) {
          recursiveTimeOut();
        } else {
          if (source.get(`layer[map].scale`).scale === 1) {
            this.map_point_changeSize(point.id);
          }
        }
      }, 100);
    };
    setTimeout(() => {
      this.map_point_changeSize(point.id);
    }, 2000);
    setTimeout(() => {
      this.map_point_changeSize(point.id);
    }, 3000);

    recursiveTimeOut();
  }

  map_point_changeSize(pointId: string, split?: boolean): boolean {
    if (pointId) {
      const source = split ? this._secondPano || null : this._mainPano || null;

      let p;
      if (source && typeof source.get === 'function') {
        p = source?.get(`layer[p_${pointId}]`);
      }

      const map = typeof source.get === 'function' ? source?.get(`layer[map]`) : null;
      if (!p || !(p?.width * map?.scale)) {
        return false;

        this.pointScale = 5 / (p.width * map?.scale);
      }
    }
  }

  map_removePoints(map?: boolean, split?: boolean): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    if (split) {
      this.activePoint[1] = null;
      if (this.pointsOnMapSplit.length > 0) {
        source?.call('removelayer(activespot)');
        this.pointsOnMapSplit.forEach((point: ProjectFloorPoint) => {
          source?.set(`layer[p_${point.id}].keep`, false);
          source?.call(`removelayer(p_${point.id})`, true);
        });
      }
      if (this.pointsOnMap.length > 0) {
        source?.call('removelayer(activespot)');
        this.pointsOnMap.forEach((point: ProjectFloorPoint) => {
          source?.set(`layer[p_${point.id}].keep`, false);
          source?.call(`removelayer(p_${point.id})`, true);
        });
      }
    } else {
      if (this.pointsOnMap.length > 0) {
        source?.call('removelayer(activespot)');
        this.pointsOnMap.forEach((point: ProjectFloorPoint) => {
          source?.set(`layer[p_${point.id}].keep`, false);
          source?.call(`removelayer(p_${point.id})`, true);
        });
      }
    }

    if (map) {
      source?.call('removelayer(map)', true);
    }
  }

  /** Resets the active minimap dot of one viewer to its normal icon and forgets it. */
  private map_clearActivePoint(source: any, split: boolean): void {
    const index = split ? 1 : 0;
    const previous = this.activePoint[index];
    if (!previous) {
      return;
    }
    if (source.get(`layer[p_${previous.id}]`)) {
      const icon =
        previous.is_selected_point === 1
          ? 'camicon-starred.png'
          : !isViewer360PlaceholderImage(previous.image)
          ? 'camicon.png'
          : 'camicon_empty.png';
      source.set(`layer[p_${previous.id}].url`, `assets/krpano/media/${icon}`);
      source.set(`layer[p_${previous.id}].zorder`, 1);
    }
    if (!split && source.get('layer[radar]')) {
      source.call('removelayer(radar)');
    }
    this.activePoint[index] = null;
  }

  // change the point on the floorMap
  map_activePoint(point: ProjectFloorPoint, split: boolean = false, image?: string): void {
    this.pointCount = 0;
    const source = split ? this._secondPano : this._mainPano;
    if (!source || !point?.id) {
      return;
    }
    const currentPointLayer = source.get(`layer[p_${point.id}]`);
    if (!currentPointLayer) {
      // The frame is not on the minimap (e.g. a video frame outside the AI points set):
      // clear the previous active dot instead of leaving it highlighted.
      this.map_clearActivePoint(source, split);
      return;
    }
    // change back the previous point to normal
    if (this.activePoint[0] && !split) {
      const icon =
        this.activePoint[0].is_selected_point === 1
          ? 'camicon-starred.png'
          : !isViewer360PlaceholderImage(this.activePoint[0].image)
          ? 'camicon.png'
          : 'camicon_empty.png';
      source.set(`layer[p_${this.activePoint[0].id}].url`, `assets/krpano/media/${icon}`);
      source.set(`layer[p_${point.id}].zorder`, 1);
    }

    if (this.activePoint[1] && split) {
      const icon =
        this.activePoint[1].is_selected_point === 1
          ? 'camicon-starred.png'
          : !isViewer360PlaceholderImage(this.activePoint[1].image)
          ? 'camicon.png'
          : 'camicon_empty.png';
      source.set(`layer[p_${this.activePoint[1].id}].url`, `assets/krpano/media/${icon}`);
      source.set(`layer[p_${point.id}].zorder`, 1);
    }

    // change the current point to active by changing the icon
    if (split === true) {
      const splitPoint = structuredClone(point);
      if (!splitPoint) {
        return;
      }
      splitPoint.image = structuredClone(image ?? this._project.secondPImg?.image_360);
      this.activePoint[1] = splitPoint;
    } else {
      const mainPoint = structuredClone(point);
      if (!mainPoint) {
        return;
      }
      mainPoint.image = structuredClone(this._project.currentPImg?.image_360);
      this.activePoint[0] = mainPoint;
    }

    setTimeout(() => {
      source.set(`layer[p_${point.id}].url`, `assets/krpano/media/camicon_selected_2.png`);

      source.set(`layer[p_${point.id}].zorder`, 3);
    }, 500);

    if (this.getRadarPermission()) {
      !split ? this.addRadar(source) : null;
    }

    // const pointSize = this.calcPointSize(point);
    // // active mapspot to show which point is active
    // const p = source.get(`layer[p_${point.id}]`);
    // const map = source.get(`layer[map]`);
    // if (source.get(`layer[p_${point.id}]`)) {
    //   if (!source.get(`layer[activespot]`)) {
    //     source.call(`addlayer(activespot)`);
    //     if (this.getRadarPermission()) {
    //       this.addRadar(source);
    //     }
    //     source.set(`layer[activespot].sacle, 0.25`);
    //     // source.set(`layer[activespot].width, ${pointSize.width}`);
    //     // source.set(`layer[activespot].height, ${pointSize.height}`);
    //     // source.call(`tween(layer[activespot].scale, ${0.8},0)`);
    //     source.call(`tween(layer[activespot].x, ${-14},0)`);
    //     source.call(`tween(layer[activespot].y, ${-14},0)`);
    //   }
    // }
    // source.set(`layer[activespot].parent`, `p_${point.id}`);
    // source.set(
    //   `layer[activespot].url`,
    //   `assets/krpano/media/camicon_selected.png`,
    // );
  }

  addRadar(source) {
    source.call(`addlayer(radar)`);
    source.set(`layer[radar].name`, `radar`);
    source.set(`layer[radar].zorder`, 1);
    source.set(`layer[radar].alpha`, 0.75);
    source.set(`layer[radar].align`, 'center');
    source.set(`layer[radar].keep`, false);
    source.set(`layer[radar].heading`, this._project?.currentPImg?.heading_angle || 0);
    source.set(`layer[radar].fillcolor`, this._auth._persona.colorAccent1.replace(/#/g, '0x'));
    source.set(`layer[radar].linewidth`, `1`);
    source.set(`layer[radar].linecolor`, this._auth._persona.colorPrimary1.replace(/#/g, '0x'));

    source.set(`layer[radar].childorder`, 5);
    source.set(`layer[radar].parent`, `layer[p_${this.activePoint[0].id}]`);
    source.set(`layer[radar].linealpha`, `1`);
    source.set(`layer[radar].scale`, `0.75`);
    source.set(`layer[radar].url`, `/assets/krpano/plugins/radar.js`);
  }

  getRadarPermission(): boolean {
    if (this._project.currentPImg?.report?.report_type === REPORT_TYPE.VIDEOS) {
      return true;
    } else {
      return false;
    }
  }

  shouldPreserveRadar(): boolean {
    return this._loading && this.getRadarPermission() && !!this.activePoint[0];
  }

  map_pointNoImg(point: ProjectFloorPoint, split?: boolean): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    if (source.get(`layer[p_${point.id}]`)) {
      source.set(`layer[p_${point.id}].url`, `assets/krpano/media/camicon_empty.png`);
      // source.set(`layer[activespot].scale`, `0.50`);
    }
  }

  map_pointWithImg(point: ProjectFloorPoint, split?: boolean): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    if (source.get(`layer[p_${point.id}]`)) {
      source.set(`layer[p_${point.id}].url`, `assets/krpano/media/camicon.png`);
      // source.set(`layer[activespot].scale`, `0.50`);
    }
  }

  map_toggle(val: boolean): void {
    this.mainPano.set('layer[box].visible', val);
    this.secondPano?.set('layer[box].visible', val);
  }

  /* ******************
   *  *** Navigation Hotspots  ***
   *  ****************** */
  addNavigationHotspot(
    panoId: string,
    hotspot: NavigationHotspot,
    pointName: string,
    currentPImg: PointImage,
    onClickCallBack?: (hotspot: NavigationHotspot) => void,
  ): void {
    const source = this.getPano(panoId);
    source.call(`addhotspot(h_${hotspot.id})`);

    source.set(`hotspot[h_${hotspot.id}].name`, `h_${hotspot.id}`);
    source.set(`hotspot[h_${hotspot.id}].url`, `assets/krpano/media/hotspot.png`);

    source.set(`hotspot[h_${hotspot.id}].distorted`, true);
    source.set(`hotspot[h_${hotspot.id}].zoom`, true);
    source.set(`hotspot[h_${hotspot.id}].ath`, hotspot.ath);
    source.set(`hotspot[h_${hotspot.id}].atv`, -hotspot.atv);
    source.set(
      `hotspot[h_${hotspot.id}].onhover`,
      `showtext(
          <span style="font-weight: normal; font-size: smaller">${pointName}</span>
          <br>
          <span style="font-weight: normal; font-size: smaller">Ath ${hotspot.ath}</span>
          <br>
          <span style="font-weight: normal; font-size: smaller">Atv ${hotspot.atv}</span>
            </p> ,STYLE4);`,
    );
    source.set(`hotspot[h_${hotspot.id}].onclick`, () => {
      if (onClickCallBack) {
        onClickCallBack(hotspot);
      }
    });
  }

  /* ******************
   *  *** Observations  ***
   *  ****************** */

  /**
   * Maps observation status (English or localized ar/ru) to the canonical icon key
   * so the 360 viewer shows the correct color (e.g. orange for in_progress) in all locales.
   */
  private getObservationStatusForIcon(status: string): string {
    return statusToIconKey(status);
  }

  addObservation(
    panoId: string,
    observation: ObservationRefactor,
    visible = true,
    onClickCallBack?: (observation: ObservationRefactor) => void,
  ): void {
    let titlecase = new TitleCasePipe();
    const _t = (word) => this.translocoService.translate(word);
    const dir = this.translocoService.getActiveLang() === 'ar' ? 'rtl' : 'ltr';

    // observation.title = upperFirst(observation.title);
    observation.significance = upperFirst(titlecase.transform(observation.significance_label));

    const source = this.getPano(panoId);
    if (!source) {
      return;
    }

    const statusForIcon = this.getObservationStatusForIcon(observation.status);

    source.call(`addhotspot(t_${observation.id})`);
    source.set(
      `hotspot[t_${observation.id}].url`,
      observation.type === 'note'
        ? 'assets/krpano/media/note.png'
        : `assets/krpano/media/alert-${statusForIcon}.png`,
    );

    this._mainPano.set(`hotspot[t_${observation.id}].visible`, visible);
    // TODO: add here

    if (isAiGenerated(observation)) {
      // PPE auto-detection (V2CORE-2901): AI icon per status and an "AI · NN%" hover line.
      // Checked first so AI titles never fall into the legacy title-based branches below.
      source.set(`hotspot[t_${observation.id}].url`, aiObservationPinUrl(observation));
      source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      source.set(`hotspot[t_${observation.id}].zoom`, true);
      source.set(`hotspot[t_${observation.id}].distorted`, false);
      const title = observation.title ?? '';
      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${title.length > 50 ? title.slice(0, 40) + '...' : title}
            <p dir="${dir}">
                <span style="font-weight: bold; font-size: smaller">${aiObservationPinHover(
                  observation,
                  (key, params) => this.translocoService.translate(key, params),
                )}</span>
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  ${startCase(observation.creator?.name ?? 'WhiteHelmet AI')}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Status',
                )}:</span> ${_t(startCase(observation.status))}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Significance',
                )}:</span> ${observation.significance_label}
                <br> <span style="font-weight: normal; font-size: smaller">${_t('Type')}:</span> ${
                  observation.type_label
                }
                ${
                  observation.due_date && observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    } else if (
      observation.creator.uuid === 'e9dc50ed-7079-43ba-ba85-dd6c2ef51b11' ||
      observation.unique_id === 'WH-8170' ||
      observation.unique_id === 'WH-9810' ||
      observation.unique_id === 'WH-5271'
    ) {
      if (observation.title === 'POOR HOUSEKEEPING' || observation.unique_id === 'WH-9810') {
        source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/poor.png`);
      }
      if (observation.title === 'NOT WEARING THE VEST') {
        source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/vest.png`);
      }
      if (
        observation.title === 'NOT WEARING SAFETY HELMET' ||
        observation.unique_id === 'WH-8170'
      ) {
        source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/alert-helmet.png`);
      }
      if (observation.unique_id === 'WH-5271') {
        source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/honey.png`);
      }

      source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      source.set(`hotspot[t_${observation.id}].opacity`, 0.65);
      source.set(`hotspot[t_${observation.id}].zoom`, true);
      source.set(`hotspot[t_${observation.id}].distorted`, false);
      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${
                observation.title.length > 50
                  ? observation.title.slice(0, 40) + '...'
                  : observation.title
              }
            <p dir="${dir}">
                <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  WhiteHelmet AI
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Significance',
                )}:</span> ${observation.significance_label}
                <br> <span style="font-weight: normal; font-size: smaller">${_t('Type')}:</span> ${
                  observation.type_label
                }
                ${
                  observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    } else if (observation.title === 'NOT WEARING SAFETY HELMET') {
      source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/noHelmetHotspot.png`);
      if (
        observation.unique_id === 'WH-1993' ||
        observation.unique_id === 'WH-5332' ||
        observation.unique_id === 'WH-1865'
      ) {
        source.set(`hotspot[t_${observation.id}].scale`, 0.2);
      } else if (
        observation.unique_id === 'WH-6942' ||
        observation.unique_id === 'WH-2332' ||
        observation.unique_id === 'WH-3335' ||
        observation.unique_id === 'WH-9139'
      ) {
        source.set(`hotspot[t_${observation.id}].scale`, 0.3);
      } else if (
        observation.unique_id === 'WH-3126' ||
        observation.unique_id === 'WH-9822' ||
        observation.unique_id === 'WH-9679' ||
        observation.unique_id === 'WH-4928'
      ) {
        source.set(`hotspot[t_${observation.id}].scale`, 0.05);
      } else if (observation.unique_id === 'WH-2757' || observation.unique_id === 'WH-2697') {
        source.set(`hotspot[t_${observation.id}].scale`, 0.03);
      } else {
        source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      }

      source.set(`hotspot[t_${observation.id}].opacity`, 0.65);
      source.set(`hotspot[t_${observation.id}].zoom`, true);
      source.set(`hotspot[t_${observation.id}].distorted`, false);

      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${
                observation.title.length > 50
                  ? observation.title.slice(0, 40) + '...'
                  : observation.title
              }
            <p dir="${dir}">
                <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  ${startCase('WhiteHelmet AI')}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Status',
                )}:</span> ${_t(startCase(observation.status))}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Significance',
                )}:</span> ${observation.significance_label}
                <br> <span style="font-weight: normal; font-size: smaller">${_t('Type')}:</span> ${
                  observation.type_label
                }
                ${
                  observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    } else if (
      (observation.report.report_id === '7892d106-b9cf-4ac7-aeac-b89381edbf46' ||
        observation.report.report_id === '66bbf02e-d8c5-42df-8962-c78a9ec9eac4') &&
      observation.title === 'NOT WEARING SAFETY VEST'
    ) {
      source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/noVest.png`);
      // if (observation.unique_id === 'WH-8676' || observation.unique_id === 'WH-1360'|| observation.unique_id === 'WH-3749'
      //   || observation.unique_id === 'WH-4578') {
      //   source.set(`hotspot[t_${observation.id}].scale`, 0.4);
      // }

      if (
        observation.unique_id === 'WH-9678' ||
        observation.unique_id === 'WH-7393' ||
        observation.unique_id === 'WH-1841'
      ) {
        source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      } else {
        source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      }
      source.set(`hotspot[t_${observation.id}].opacity`, 0.65);
      source.set(`hotspot[t_${observation.id}].zoom`, true);
      source.set(`hotspot[t_${observation.id}].distorted`, false);

      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${
                observation.title.length > 50
                  ? observation.title.slice(0, 40) + '...'
                  : observation.title
              }
            <p dir="${dir}">
                <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  ${startCase('WhiteHelmet AI')}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Status',
                )}:</span> ${_t(startCase(observation.status))}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Priority',
                )}:</span> ${_t(observation.significance)}
                ${
                  observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    } else if (observation.title === 'POOR HOUSEKEEPING') {
      source.set(`hotspot[t_${observation.id}].url`, `assets/krpano/media/poor.png`);
      source.set(`hotspot[t_${observation.id}].scale`, 0.5);
      source.set(`hotspot[t_${observation.id}].zoom`, true);
      source.set(`hotspot[t_${observation.id}].distorted`, false);

      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${
                observation.title.length > 50
                  ? observation.title.slice(0, 40) + '...'
                  : observation.title
              }
            <p dir="${dir}">
                <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  ${startCase('WhiteHelmet AI')}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Status',
                )}:</span> ${_t(startCase(observation.status))}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Significance',
                )}:</span> ${observation.significance_label}
                <br> <span style="font-weight: normal; font-size: smaller">${_t('Type')}:</span> ${
                  observation.type_label
                }
                ${
                  observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    } else if (
      observation.unique_id !== 'WH-8170' &&
      observation.unique_id !== 'WH-9810' &&
      observation.unique_id !== 'WH-5271'
    ) {
      source.set(
        `hotspot[t_${observation.id}].url`,
        observation.type === 'note'
          ? 'assets/krpano/media/note.png'
          : `assets/krpano/media/alert-${statusForIcon}.png`,
      );
      source.set(`hotspot[t_${observation.id}].scale`, 0.1);
      source.set(
        `hotspot[t_${observation.id}].onhover`,
        `showtext(
              <span style="font-weight: normal; font-size: smaller">#${
                observation.unique_id
              } </span>  <br> ${
                observation.title.length > 50
                  ? observation.title.slice(0, 40) + '...'
                  : observation.title
              }
            <p dir="${dir}">
                <span style="font-weight: normal; font-size: smaller">${_t(
                  'Created By',
                )}:</span>  ${startCase(observation.creator.name)}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Status',
                )}:</span> ${_t(startCase(observation.status))}
                <br> <span style="font-weight: normal; font-size: smaller">${_t(
                  'Significance',
                )}:</span> ${observation.significance_label}
                <br> <span style="font-weight: normal; font-size: smaller">${_t('Type')}:</span> ${
                  observation.type_label
                }
                ${
                  observation.due_date !== 'N/A'
                    ? '<br> <span style="font-weight: normal; font-size: smaller">' +
                      _t('Due Date') +
                      ':</span> ' +
                      GStatic.transformDate(observation.due_date)
                    : ''
                }
            </p> ,STYLE4);`,
      );
    }

    // AI rows store AI degrees and are converted to krpano angles; hand-made rows are unchanged.
    const pinAngles = observationPinAngles(observation);
    source.set(`hotspot[t_${observation.id}].ath`, pinAngles.ath);
    source.set(`hotspot[t_${observation.id}].atv`, pinAngles.atv);
    source.set(`hotspot[t_${observation.id}].onclick`, () => {
      if (onClickCallBack) {
        onClickCallBack(observation);
      }
    });
  }

  addMeasurePoint(panoId, point_x, point_y) {
    const source = this.getPano(panoId);
    if (this.pointCount < 2) {
      source.call(`addhotspot(t_${this.pointCount})`);
      source.set(`hotspot[t_${this.pointCount}].url`, 'assets/krpano/media/camicon.png');
      source.set(`hotspot[t_${this.pointCount}].visible`, true);
      source.set(`hotspot[t_${this.pointCount}].scale`, 0.1);
      source.set(`hotspot[t_${this.pointCount}].opacity`, 0.65);
      source.set(`hotspot[t_${this.pointCount}].zoom`, true);
      source.set(`hotspot[t_${this.pointCount}].distorted`, false);
      source.set(`hotspot[t_${this.pointCount}].ath`, point_x);
      source.set(`hotspot[t_${this.pointCount}].atv`, point_y);
      this.pointCount += 1;
    }
    if (this.pointCount === 2 && this._project.currentPImg) {
      source.set(`hotspot[t_${0}].visible`, false);
      source.set(`hotspot[t_${1}].visible`, false);
      /*point 13 report 3 basement*/
      if (this._project.currentPImg.point_id === '6d48414e-6026-45ca-9280-d1826b779886') {
        source.call(`addhotspot(t_measure1)`);
        source.set(`hotspot[t_measure1].url`, 'assets/krpano/media/measure_1.png');
        source.set(`hotspot[t_measure1].zoom`, true);
        source.set(`hotspot[t_measure1].distorted`, true);
        source.set(`hotspot[t_measure1].opacity`, 0.65);
        source.set(`hotspot[t_measure1].ath`, -44);
        source.set(`hotspot[t_measure1].rz`, -5);
        source.set(`hotspot[t_measure1].scale`, 0.48);
        source.set(`hotspot[t_measure1].atv`, 24);
      }
      /*point 5 report 3*/
      if (this._project.currentPImg.point_id === 'f505fc39-6067-4ffb-a666-034e24036608') {
        source.call(`addhotspot(t_measure1)`);
        source.set(`hotspot[t_measure1].url`, 'assets/krpano/media/measure_2.png');
        source.set(`hotspot[t_measure1].zoom`, true);
        source.set(`hotspot[t_measure1].distorted`, true);
        source.set(`hotspot[t_measure1].visible`, true);
        source.set(`hotspot[t_measure1].scale`, 0.09);
        source.set(`hotspot[t_measure1].opacity`, 0.65);
        source.set(`hotspot[t_measure1].ath`, -22);
        source.set(`hotspot[t_measure1].rz`, -1.5);
        source.set(`hotspot[t_measure1].atv`, -3.5);
      }

      /*point 5 report 3*/
      if (this._project.currentPImg.point_id === 'c5ed4b68-e72b-49bf-a155-6f0d58212baf') {
        source.call(`addhotspot(t_measure1)`);
        source.set(`hotspot[t_measure1].url`, 'assets/krpano/media/measure_3.png');
        source.set(`hotspot[t_measure1].zoom`, true);
        source.set(`hotspot[t_measure1].distorted`, true);
        source.set(`hotspot[t_measure1].visible`, true);
        source.set(`hotspot[t_measure1].scale`, 0.2);
        source.set(`hotspot[t_measure1].opacity`, 0.65);
        source.set(`hotspot[t_measure1].rz`, 0);
        source.set(`hotspot[t_measure1].atv`, -15.5);
        source.set(`hotspot[t_measure1].ath`, 185.4);
      }

      /*point 4720 report ground video*/
      if (this._project.currentPImg.point_id === 'c527dac3-0241-42ed-a051-2e33438315b0') {
        source.call(`addhotspot(t_measure1)`);
        source.set(`hotspot[t_measure1].url`, 'assets/krpano/media/measure_4.png');
        source.set(`hotspot[t_measure1].zoom`, true);
        source.set(`hotspot[t_measure1].distorted`, true);
        source.set(`hotspot[t_measure1].visible`, true);
        source.set(`hotspot[t_measure1].opacity`, 0.65);
        source.set(`hotspot[t_measure1].ath`, 181.4);
        source.set(`hotspot[t_measure1].scale`, 0.11);
        source.set(`hotspot[t_measure1].rz`, 0);
        source.set(`hotspot[t_measure1].atv`, -3.5);
      }

      /*point 6781 report ground video*/
      if (this._project.currentPImg.point_id === '807e7b6f-e00c-4cc4-bc6b-a99b01f78eb2') {
        source.call(`addhotspot(t_measure1)`);
        source.set(`hotspot[t_measure1].url`, 'assets/krpano/media/measure_5.png');
        source.set(`hotspot[t_measure1].zoom`, true);
        source.set(`hotspot[t_measure1].distorted`, true);
        source.set(`hotspot[t_measure1].visible`, true);
        source.set(`hotspot[t_measure1].opacity`, 0.65);
        source.set(`hotspot[t_measure1].ath`, -32);
        source.set(`hotspot[t_measure1].scale`, 0.22);
        source.set(`hotspot[t_measure1].rz`, -5);
        source.set(`hotspot[t_measure1].atv`, -15.5);
      }
    }
  }

  showHideObservation(observationId: string, val: boolean, split: boolean = false): void {
    const source = split ? this._secondPano : this._mainPano;
    if (!source) {
      return;
    }
    source.set(`hotspot[t_${observationId}].visible`, val);
  }

  showHideAllPoints(observationId: string, val: boolean): void {
    this._mainPano.set(`hotspot[t_${observationId}].visible`, val);
  }

  /* ******************
   *  *** Other  ***
   *  ****************** */
  startAutoTour(start?: boolean): boolean {
    if (start === undefined) {
      this.mainPano.call('switch(autorotate.enabled)');
    } else {
      this.mainPano.set('autorotate.enabled', start);
    }
    return this.mainPano.get('autorotate.enabled');
  }

  resetPanos(): void {
    this._panosList = {};
    this._mainPano = {};
    this._secondPano = null;
    this._panosImgs = {};
    this._loaded = {};
    this._isLoaded.next({});
    this._loading = true;
    this._isLoading.next(this._loading);
    // this._event = new BehaviorSubject<{ eventName?: string; data?: any }>({});
    this.pointsOnMap = [];
    this.pointsOnMapSplit = [];
    this.minZoomSlider = 0;
  }

  removePano(panoId): void {
    if (this._panosList[panoId]) {
      (window as any).removepano(panoId);
      this._panosList[panoId] = undefined;
      delete this._panosList[panoId];
    }
  }

  removeRadar(split?: boolean): void {
    // Don't remove radar if we're currently loading images and have radar permission
    if (this._loading && this.getRadarPermission() && !!this.activePoint[0]) {
      return;
    }
    split ? this._secondPano.call('removelayer(radar)') : this._mainPano.call('removelayer(radar)');
  }

  announceCoordinate(): any {
    {
      for (let i = 0; i <= 1; i++) {
        const boundingBox = this.viewer?.model?.getBoundingBox(); // Get the bounding box of the model
        const diagonal = boundingBox?.max?.distanceTo(boundingBox.min); // Calculate the diagonal distance of the bounding box
        this.viewer?.navigation.fitBounds(true, boundingBox, true, true);

        this.viewer?.navigation.setView(
          new THREE.Vector3(0, 0, diagonal),
          new THREE.Vector3(0, 0, 0),
        ); // Set the camera position and target
      }
    }
  }

  updateBimCam(x: any, y: any, z: any): any {
    if (x && y && z) {
      x = parseFloat(x);
      y = parseFloat(y);
      z = parseFloat(z);
      this.viewer?.navigation?.setView(new THREE.Vector3(x, y, z), new THREE.Vector3(x, y + 1, z));
      this.viewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1)); // Set the up vector to (0, 0, 1) for horizontal orientation
    }
  }

  private resolveZ(x: number, y: number, z: number): Promise<number | null> {
    return new Promise((resolve) => {
      const viewer = this.viewer;
      const model = viewer?.model;
      const impl = viewer?.impl;

      if (!viewer || !model || !impl) {
        resolve(null);
        return;
      }

      const bbox = model.getBoundingBox?.();
      const originZ = z;

      const origin = new THREE.Vector3(x, y, originZ);
      const direction = new THREE.Vector3(0, 0, -1);

      try {
        const ray = new THREE.Ray(origin, direction);
        const result =
          typeof impl.rayIntersect === 'function' ? impl.rayIntersect(ray, true) : null;
        resolve(result?.intersectPoint?.z ?? null);
      } catch (error) {
        console.error('resolveZ rayIntersect failed', error);
        resolve(null);
      }
    });
  }

  async updateBimCamWithTarget(
    x: any,
    y: any,
    z: any,
    headingAngle: any,
    floorRotation: string,
  ): Promise<void> {
    const parsedX = parseFloat(x);
    const parsedY = parseFloat(y);
    const parsedZ = parseFloat(z);

    if (!Number.isFinite(parsedX) || !Number.isFinite(parsedY)) {
      return;
    }

    const model = this.viewer?.model;
    const snapRange = feetToModelUnits(model, CAMERA_FLOOR_SNAP_RANGE_FT);
    const resolvedZ = await this.resolveZ(parsedX, parsedY, parsedZ);
    const baseZ =
      Number.isFinite(resolvedZ) && Math.abs(resolvedZ - parsedZ) <= snapRange
        ? resolvedZ
        : parsedZ;

    if (!Number.isFinite(baseZ)) {
      return;
    }

    const eyeHeight = feetToModelUnits(model, CAMERA_EYE_HEIGHT_FT);
    const offset = feetToModelUnits(model, CAMERA_FLOOR_CLEARANCE_FT);
    const cameraZ = baseZ + eyeHeight + offset;

    const rotationAngle = floorRotation ? parseFloat(floorRotation) : 0;
    const normalizedHeading = this.normalizeAngle(parseFloat(headingAngle) || 0);
    const position = new THREE.Vector3(parsedX, parsedY, cameraZ);

    let target = this.calculateTargetFromAngles(
      position,
      rotationAngle,
      0,
      normalizedHeading,
      this._project.selectedFloor.rotation_angle_degrees,
    );
    if (this._project.selectedReport.report_type === REPORT_TYPE.IMAGE) {
      target = this.applyOffsetToNewKrpanoTarget(
        target,
        position,
        this._project.currentPImg.bim_offset ?? this._project.currentPImg.default_bim_offset,
      );
    }

    this.viewer?.navigation?.setView(position, target);
    this.viewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1));

    setTimeout(() => {
      this.updateForgeFromKrpano();
    });
  }

  normalizeAngle(angle) {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
  }

  calculateFocalLength(fov: any) {
    const sensorWidth = 5.76; // Replace with the actual sensor width in your case
    const halfFOV = fov / 2;
    return sensorWidth / (2 * Math.tan(halfFOV));
  }

  updateSceneView(heading: number) {
    setTimeout(() => {
      if (this._secondPano) {
        this._mainPano.call(`tween(view.hlookat, ${heading})`);
        this._secondPano.call(`tween(view.hlookat, ${heading})`);
      }
    }, 500);
  }

  openPostProcessing() {
    const state = this.mainPano.get('layer[pp_demo_container].visible');
    this.mainPano.set('layer[pp_demo_container].visible', !state);
    // Update labels when opening post-processing panel
    this.updatePostProcessingLabels();
  }

  updatePostProcessingLabels() {
    if (!this._mainPano || !this._mainPano.set) return;

    const lang = this.translocoService.getActiveLang();

    // Helper function to get translation with fallback
    const translateWithFallback = (key: string, fallback: string): string => {
      try {
        const translated = this.translocoService.translate(key);
        // If translation returns null, undefined, empty string, or the key itself, use fallback
        if (
          !translated ||
          translated === key ||
          (typeof translated === 'string' && translated.trim() === '')
        ) {
          return fallback;
        }
        // Also check if translated is the string "null" (edge case)
        if (translated === 'null' || translated === 'undefined') {
          return fallback;
        }
        return translated;
      } catch (e) {
        // If translation fails, use fallback
        GStatic.log('Translation error for key:', key, e);
        return fallback;
      }
    };

    // Get translations for all languages with fallbacks
    const postprocessingText = translateWithFallback('Postprocessing', 'Postprocessing');
    const originalText = translateWithFallback('Original', 'Original');
    const exposureText = translateWithFallback('Exposure: ', 'Exposure: ');
    const lightsText = translateWithFallback('Lights: ', 'Lights: ');
    const shadowsText = translateWithFallback('Shadows: ', 'Shadows: ');
    const sharpeningText = translateWithFallback('Sharpening: ', 'Sharpening: ');

    // Set krpano data variables for use in calc() functions
    try {
      // Ensure all values are valid strings before setting
      const safeSet = (path: string, value: string) => {
        if (value && typeof value === 'string' && value !== 'null' && value !== 'undefined') {
          this._mainPano.set(path, value);
        } else {
          GStatic.log('Warning: Attempted to set invalid value to', path, ':', value);
        }
      };

      // Set data variables first (these are used by getvar() functions)
      safeSet('data[pp_labels].label_postprocessing', postprocessingText);
      safeSet('data[pp_labels].label_original', originalText);
      safeSet('data[pp_labels].label_exposure', exposureText);
      safeSet('data[pp_labels].label_lights', lightsText);
      safeSet('data[pp_labels].label_shadows', shadowsText);
      safeSet('data[pp_labels].label_sharpening', sharpeningText);

      // Update static labels directly (this ensures they show immediately)
      safeSet('layer[pplc_info].html', postprocessingText);
      safeSet('layer[pplc_info_toggle].html', originalText);

      // Trigger getvar and setvar to refresh the dynamic control labels
      // This ensures the labels are displayed immediately with the correct translation
      // Use multiple attempts to ensure layers are ready
      const refreshLabels = () => {
        try {
          // Call getvar first to copy the label_text, then setvar to update the HTML display
          this._mainPano.call('layer[pplc_exposure].getvar()');
          this._mainPano.call('layer[pplc_exposure].setvar()');
          this._mainPano.call('layer[pplc_lights].getvar()');
          this._mainPano.call('layer[pplc_lights].setvar()');
          this._mainPano.call('layer[pplc_shadows].getvar()');
          this._mainPano.call('layer[pplc_shadows].setvar()');
          this._mainPano.call('layer[ppsc_strength].getvar()');
          this._mainPano.call('layer[ppsc_strength].setvar()');
        } catch (e) {
          // Layers might not be loaded yet, will be initialized when they load
          GStatic.log('Layers not ready yet:', e);
        }
      };

      // Try multiple times with increasing delays
      setTimeout(refreshLabels, 50);
      setTimeout(refreshLabels, 150);
      setTimeout(refreshLabels, 300);
      setTimeout(refreshLabels, 500);
    } catch (error) {
      GStatic.log('Error updating post-processing labels:', error);
    }
  }

  filterObservations(observations, status: string) {
    const source = this._mainPano;
    observations.forEach((observation: Observation) => {
      if (observation.status === status || status === 'all') {
        source.set(`hotspot[t_${observation.id}].visible`, true);
      } else {
        source.set(`hotspot[t_${observation.id}].visible`, false);
      }
    });
  }

  setCameraBIM(viewer: Autodesk.Viewing.Viewer3D) {
    this.viewer = viewer;
  }

  logPanoMovement() {
    let hlookat = this.mainPano.get('view.hlookat');
    let vlookat = this.mainPano.get('view.vlookat');
    // console.log('hlookat', hlookat, 'vlookat', vlookat,);

    let hlookat2 = this.secondPano.get('view.hlookat');
    let vlookat2 = this.secondPano.get('view.vlookat');
    // console.log('hlookat2', hlookat2, 'vlookat2', vlookat2,);
  }

  //updating BIM camera corresponding to Krpano viewer
  updateForgeFromKrpano(e?: MouseEvent) {
    if (!this.lockBim) {
      return;
    }
    if (e instanceof MouseEvent && e.buttons !== 1) {
      return;
    }
    if (this.rafId !== null) {
      return;
    }
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      this.syncForgeCameraWithKrpano();
    });
  }

  private syncForgeCameraWithKrpano() {
    let hlookat = this.mainPano.get('view.hlookat');
    let vlookat = this.mainPano.get('view.vlookat');
    const camera = this.viewer?.navigation?.getCamera();
    if (camera !== undefined) {
      let target = this.calculateTargetFromAngles(
        camera?.position,
        hlookat,
        vlookat,
        this._project.currentPImg.heading_angle,
        this._project.selectedFloor.rotation_angle_degrees,
      );

      if (this._project.selectedReport.report_type === REPORT_TYPE.IMAGE) {
        let custom_offset = this._project.currentPImg.bim_offset;
        let default_offset = this._project.currentPImg.default_bim_offset;
        target = this.applyOffsetToNewKrpanoTarget(
          target,
          camera?.position,
          custom_offset ?? default_offset, //checking if the point has custom offset or not
        );
      }

      this.viewer?.navigation?.setTarget(target);
      this.viewer?.navigation?.setCameraUpVector(new THREE.Vector3(0, 0, 1)); // Set the up vector to (0, 0, 1) for horizontal orientation
      this.viewer?.navigation?.setFocalLength(7.2);
    }
  }

  applyOffsetToNewKrpanoTarget(
    krpanoTarget: THREE.Vector3,
    forgePosition: THREE.Vector3,
    offsetAngle: number,
  ): THREE.Vector3 {
    // Step 1: Calculate the direction vector from Forge's position to the new krpano target
    let direction = new THREE.Vector3().subVectors(krpanoTarget, forgePosition);

    // Step 2: Create a quaternion (rotation) using the offset angle
    let quaternion = new THREE.Quaternion();
    quaternion.setFromAxisAngle(new THREE.Vector3(0, 0, 1), offsetAngle * (Math.PI / 180)); // Assuming rotation around Z-axis

    // Step 3: Apply the rotation to the direction vector
    direction.applyQuaternion(quaternion);

    // Step 4: Calculate the new Forge target by adding the rotated direction vector to Forge's position
    return new THREE.Vector3().addVectors(forgePosition, direction);
  }

  calculateTargetFromAngles(
    position: THREE.Vector3,
    hlookat: number,
    vlookat: number,
    headingAngle,
    floorRotation,
  ): THREE.Vector3 {
    let rotationAngle = parseFloat(floorRotation);
    headingAngle = this.normalizeAngle(headingAngle);
    // Convert angles to radians

    let hlookatRad;
    let dx;
    let dy;
    const vlookatRad = -vlookat * (Math.PI / 180);
    const headingRad = -headingAngle * (Math.PI / 180);
    if (this._project.selectedReport.report_type === REPORT_TYPE.IMAGE) {
      hlookatRad = -hlookat * (Math.PI / 180);
      dx = Math.cos(vlookatRad) * Math.cos(hlookatRad);
      dy = Math.cos(vlookatRad) * Math.sin(hlookatRad);
    } else {
      hlookatRad = (-hlookat + rotationAngle) * (Math.PI / 180);
      dx = Math.cos(vlookatRad) * Math.cos(hlookatRad + headingRad);
      dy = Math.cos(vlookatRad) * Math.sin(hlookatRad + headingRad);
    }

    // Calculate the direction vector using trigonometry

    const dz = Math.sin(vlookatRad);

    return new THREE.Vector3(position.x + dx, position.y + dy, position.z + dz);
  }

  openBimCompareGuide() {
    const setOrientationTour: TourGuideModel = {
      height: '700px',
      title: this._transloco.translate('BIM compare with image reports guide'),
      subtitle: this._transloco.translate(
        'In split screen mode, the 360 viewer and BIM viewers are synced to provide a cohesive viewing experience. If you encounter any issues with the sync, follow the steps below to manually adjust the views and restore proper alignment.',
      ),
      bullets: [
        {
          value: this._transloco.translate('Click the Unlock button to manually adjust the view'),
        },
        {
          value: this._transloco.translate(
            'Navigate the BIM viewer (on the left side) to the correct target or position.',
          ),
        },
        {
          value: this._transloco.translate(
            'Once the BIM viewer is correctly aligned, click the Lock button to re-enable syncing between both viewers',
          ),
        },
        {
          value: this._transloco.translate(
            'After locking, check that both the 360 viewer and BIM viewers are correctly synced and responding to interactions.',
          ),
        },
        {
          value: this._transloco.translate(
            "After locking the screens, if you still notice a syncing issue between the viewers, it's likely that the BIM viewer is flipped or rotated by 180 degrees. You can fix this by pressing the Flip button at the top right in the BIM viewer.",
          ),
        },
      ],
      image: 'assets/tour-guides/bim_tour.gif',
    };
    const dialogRef = this.dialog.open(TourGuideDialogComponent, {
      data: setOrientationTour,
      autoFocus: false, // Prevent automatic focus
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        const user = await this.userService.getCurrentUser();
        this._project.setBimCompareTourSeen(user.email);
      }
    });
  }
  // Calculate the offset when the user locks the views
  lockBimViews(
    krpanoHlookat: number,
    krpanoVlookat: number,
    krpanoHeading: number,
    forgePosition: THREE.Vector3,
    floorRotation: number,
    is_default: boolean,
  ) {
    // todo: bim-compare
    const krpanoTarget = this.calculateTargetFromAngles(
      forgePosition,
      krpanoHlookat,
      krpanoVlookat,
      krpanoHeading,
      floorRotation,
    );

    // Get Forge's current camera target
    const forgeCamera = this.viewer.navigation.getCamera();
    const forgeTarget = forgeCamera.target.clone();

    let offset = this.calculateSignedAngleBetweenTargets(forgePosition, krpanoTarget, forgeTarget);
    let reportId = this._project.selectedReport?.id;
    let pImageId = this._project.currentPImg?.id;
    this._project.setBimOffsetAngle(reportId, pImageId, offset, is_default).then();

    // Calculate the offset between krpano and Forge target
    // this.bim_offset = new THREE.Vector3().subVectors(forgeTarget, krpanoTarget);
  }

  calculateSignedAngleBetweenTargets(
    cameraPosition: THREE.Vector3,
    target1: THREE.Vector3,
    target2: THREE.Vector3,
  ): number {
    const dir1 = new THREE.Vector3().subVectors(target1, cameraPosition).setZ(0).normalize();
    const dir2 = new THREE.Vector3().subVectors(target2, cameraPosition).setZ(0).normalize();
    if (!isFinite(dir1.lengthSq()) || !isFinite(dir2.lengthSq())) return 0;

    const dot = THREE.MathUtils.clamp(dir1.dot(dir2), -1, 1);
    const crossZ = dir1.x * dir2.y - dir1.y * dir2.x;
    const angleRad = Math.atan2(crossZ, dot); //signed;
    const angleDeg = THREE.MathUtils.radToDeg(angleRad);
    return this.normalizeAngle(angleDeg);
  }
  calculateAngleBetweenTargets(
    cameraPosition: THREE.Vector3,
    target1: THREE.Vector3,
    target2: THREE.Vector3,
  ): number {
    // debugger;
    // Step 1: Calculate the direction vectors from the camera position to each target
    let direction1 = new THREE.Vector3().subVectors(target1, cameraPosition);
    let direction2 = new THREE.Vector3().subVectors(target2, cameraPosition);

    // Step 2: Calculate the dot product of the two direction vectors
    const dotProduct = direction1.dot(direction2);

    // Step 3: Calculate the magnitudes (lengths) of the direction vectors
    const magnitude1 = direction1.length();
    const magnitude2 = direction2.length();

    // Step 4: Calculate the cosine of the angle
    const cosTheta = dotProduct / (magnitude1 * magnitude2);

    // Step 5: Clamp the cosine to avoid numerical errors and calculate the angle in radians
    const clampedCosTheta = Math.max(-1, Math.min(1, cosTheta)); // Ensures value is within [-1, 1]
    const angleInRadians = Math.acos(clampedCosTheta);

    // Step 6: Convert the angle from radians to degrees
    const angleInDegrees = angleInRadians * (180 / Math.PI);

    return angleInDegrees;
  }

  //updating Krpano camera corresponding to BIM viewer
  updateKrpanoFromForge(updateOnly?: boolean): void {
    let camera = this.viewer?.navigation.getCamera();
    let headingAngle = this._project.currentPImg.heading_angle;

    let floorRotation = parseFloat(this._project.selectedFloor.rotation_angle_degrees);

    // Calculate direction vector
    const dx = camera.target.x - camera.position.x;
    const dy = camera.target.y - camera.position.y;
    const dz = camera.target.z - camera.position.z;

    // Set the target to the origin
    if (!updateOnly && this.lockBim) {
      // Calculate horizontal and vertical lookat angles
      let hlookat = Math.atan2(dy, dx) * (180 / Math.PI);
      let vlookat = Math.atan2(dz, Math.sqrt(dx * dx + dy * dy)) * (180 / Math.PI);

      // Adjust for floor rotation and heading angle
      hlookat = -hlookat + floorRotation + headingAngle;

      // Normalize horizontal angle to -180 to +180 range
      hlookat = this.normalizeAngle(hlookat);

      // Check if the direction needs to be flipped
      const currentLookat = this.mainPano.get('view.hlookat');
      if (Math.abs(currentLookat - hlookat) > 180) {
        hlookat = this.normalizeAngle360(hlookat + 180);
      }

      // Map vertical angle to -90 to +90 range
      vlookat = Math.max(Math.min(-vlookat, 90), -90);

      this.mainPano.call(`lookat(${hlookat},${vlookat},null,true)`);
    }
  }

  normalizeAngle360(angle) {
    angle = angle % 360;
    if (angle < 0) {
      angle += 360;
    }
    return angle;
  }

  addBlurFaces() {
    this._project.currentPImg.blurred_faces?.forEach((face, index) => {
      const coordinate = this.mapSquareToKrpano(
        face.x11,
        face.y11,
        face.x12,
        face.y12,
        face.x21,
        face.y21,
        face.x22,
        face.y22,
        this._project.currentPImg.width,
        this._project.currentPImg.height,
      );

      const source = this.mainPano;
      const hotspotId = `bf_${this._project.currentPImg.blurred_faces[index].face_id}`;

      source.call(`addhotspot(${hotspotId})`);
      source.set(`hotspot[${hotspotId}].type`, 'image');
      source.set(`hotspot[${hotspotId}].visible`, true);
      source.set(`hotspot[${hotspotId}].zoom`, true);
      source.set(`hotspot[${hotspotId}].distorted`, false);
      source.set(`hotspot[${hotspotId}].renderer`, 'css3d');
      source.set(`hotspot[${hotspotId}].url`, 'assets/krpano/media/blur.png');
      source.set(`hotspot[${hotspotId}].scale`, '0.05');
      source.set(`hotspot[${hotspotId}].bgblur`, '5');
      source.set(`hotspot[${hotspotId}].bgalpha`, '0.5');

      source.set(`hotspot[${hotspotId}].ath`, coordinate.ath);
      source.set(`hotspot[${hotspotId}].atv`, coordinate.atv);
    });
  }

  mapSquareToKrpano(
    x11: number,
    y11: number,
    x12: number,
    y12: number,
    x21: number,
    y21: number,
    x22: number,
    y22: number,
    imageWidth: number,
    imageHeight: number,
  ): { ath: number; atv: number } {
    const centerX = (x11 + x12 + x21 + x22) / 4;
    const centerY = (y11 + y12 + y21 + y22) / 4;

    const deltaX = centerX - imageWidth / 2;
    const deltaY = imageHeight / 2 - centerY;

    const ath = (deltaX / (imageWidth / 2)) * 180;
    const atv = (-deltaY / (imageHeight / 2)) * 90;
    return { ath, atv };
  }

  openMapBox() {
    this.dialogsRef.floorPopup = this.dialog.open(FloorPopupComponent, {
      width: '75%',
      height: '75%',
      maxWidth: '95%',
      panelClass: 'floorPanel',
      backdropClass: 'floorBackdrop',
      data: {
        type: 'points',
        project: this._project,
        currentPImg: this._project.currentPImg,
        points: this.pointsOnMap,
      },
    });
  }
  async isPointHasImageInMultiReport() {
    const pointId = this._project.currentPImg?.point_id;
    if (!pointId || pointId.startsWith('no-')) {
      this.hasMultipleImages = false;
      return;
    }
    if (this._project.currentPImg?.report?.report_type !== REPORT_TYPE.IMAGE) {
      this.hasMultipleImages = false;
      return;
    }

    try {
      const response = await this._project.getReportsWithImagesForPoint(pointId).toPromise();
      this.multiImageReports = response.data;
      this.hasMultipleImages = response.data?.length > 0;
    } catch (error) {
      this.toaster.error(error.error.message);
      this.hasMultipleImages = false;
    }
  }

  /*GET FULL PATH*/
  getFullPath(report_id: string, high_quality, floor_id: string) {
    return new Promise<any>((resolve, reject) => {
      this._projectsApi.getFullPath(report_id, high_quality, floor_id).subscribe(
        (res) => {
          resolve(res.data);
        },
        (error) => {
          console.log(error.error.message);
        },
      );
    });
  }
}
